import { QueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { membershipChangeLogKeys } from '../membershipChangeLog/keys';
import { sessionReportKeys } from './keys';
import { LessonMoveErrorCode, SessionReportLesson, SessionReportLessonTarget } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 수업 옮기기 규칙(화면과 분리한 순수 함수) — 명세 docs/SESSION_REPORT_LESSON_MOVE_PLAN.md §4-8.
 */

/** 확인 창을 열 때마다 새로 만든다. 서버 형식: 32~64자 영숫자·하이픈. */
export const newMoveId = (): string => {
    const c: Crypto | undefined = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
    if (c?.randomUUID) return c.randomUUID();
    const bytes = new Uint8Array(16);
    if (c?.getRandomValues) c.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const MOVE_ID_PATTERN = /^[A-Za-z0-9-]{32,64}$/;

const kst = (iso: string) => dayjs(iso).tz('Asia/Seoul');
export const formatLessonTime = (iso: string) => kst(iso).format('M월 D일 HH:mm');

/** 수업 날짜가 이번 달(KST)보다 앞이면 지난 달 수업 — 이미 급여를 드렸을 수 있다. */
export const isPastMonthLesson = (lessonIso: string, now: Date = new Date()) =>
    kst(lessonIso).format('YYYY-MM') < dayjs(now).tz('Asia/Seoul').format('YYYY-MM');

const dateOnly = (value: string) => (value.length === 10 ? value : kst(value).format('YYYY-MM-DD'));

/** 확인 창 경고(위에서부터 중요한 순). 항상 첫 줄은 정산·급여 영향. */
export const buildMoveWarnings = (
    lesson: Pick<SessionReportLesson, 'startTime'>,
    target: Pick<SessionReportLessonTarget, 'isExpired' | 'issueAt' | 'expirationDate' | 'expiryPolicy'>,
    now: Date = new Date(),
): string[] => {
    const warnings = ['이 수업의 수업료 정산·급여 숫자가 바뀔 수 있어요.'];
    if (isPastMonthLesson(lesson.startTime, now)) warnings.push('지난 달 수업이에요. 이미 급여를 드렸다면 차액을 따로 맞춰야 해요.');
    const lessonDay = kst(lesson.startTime).format('YYYY-MM-DD');
    if (target.issueAt && lessonDay < dateOnly(target.issueAt)) warnings.push('이 수강권을 만들기 전 수업이라, 옮기면 「발급 전 수업」으로 표시돼요.');
    if (target.expirationDate && target.expiryPolicy !== 'COUNT' && lessonDay > dateOnly(target.expirationDate)) {
        warnings.push('이 수강권의 만료일 뒤 수업이에요.');
    }
    if (target.isExpired) warnings.push('만료된 수강권이에요. 옮겨도 다시 쓸 수 있는 상태가 되지는 않아요.');
    return warnings;
};

/** 발급 기록을 확인 못 한 수강권을 옮길 때 확인 창에 보이는 설명(체크해야 옮길 수 있다). */
export const UNVERIFIED_ORIGIN_NOTICE =
    '결제·양도·인계 기록이 없는 수강권이에요. 서비스로 준 0원 수강권이면 괜찮아요. 양도가 실패해서 남은 수강권일 수도 있으니, 회원에게 실제로 준 수강권인지 확인해 주세요.';
export const UNVERIFIED_ORIGIN_CHECK = '회원에게 실제로 발급한 수강권이 맞아요';

/** 옮길 수 없는 수업의 이유 → 문구. 모르는 값(구서버 포함)은 일반 문구. */
export const describeNotMovable = (reason: string | null | undefined): string => {
    switch (reason) {
        case 'SHARED_SOURCE':
            return '공유 수강권 수업은 여기서 옮길 수 없어요';
        case 'COMPANION_SOURCE':
            return '1:2 수강권 수업은 여기서 옮길 수 없어요';
        case 'SHARED_POOL_LESSON':
            return '공유 회차로 잡은 수업은 여기서 옮길 수 없어요';
        case 'MOVE_LIMIT':
            return '여러 번 옮긴 수업이라 더 옮길 수 없어요';
        case 'NO_TARGET_FOR_COACH':
            return '이 수업 강사의 다른 수강권이 없어요';
        case 'NO_TARGET':
            return '옮길 수 있는 다른 수강권이 없어요';
        default:
            return '여기서 옮길 수 없는 수업이에요';
    }
};

/** 후보에서 빠진 수강권의 이유 → 문구. 모르는 값은 null(목록에 싣지 않는다). */
export const describeUnavailableTarget = (reason: string): string | null => {
    switch (reason) {
        case 'SHARED':
            return '공유 수강권이에요';
        case 'COMPANION':
            return '1:2 수강권이에요';
        case 'HANDOFF_SOURCE':
            return '강사를 바꾸기 전 수강권이에요';
        case 'TRANSFER_IN_FLIGHT':
            return '양도가 진행 중이거나 실패한 수강권이에요';
        case 'EXPIRED_HISTORY':
            return '만료된 수강권 중 발급 기록이 없거나 환불·양도·그룹 전환 기록이 있는 수강권이에요';
        default:
            return null;
    }
};

/** 서버가 확정한 실패 → 화면 문구. `reload` 면 목록을 다시 받는다. */
export const describeMoveError = (code: LessonMoveErrorCode): { text: string; reload: boolean } => {
    switch (code) {
        case 'SCHEDULE_MEMBERSHIP_CHANGED':
            return { text: '다른 곳에서 먼저 바뀌었어요. 목록을 새로 불러왔어요.', reload: true };
        case 'LESSON_MOVE_TARGET_FULL':
        case 'LESSON_MOVE_TARGET_NOT_ELIGIBLE':
            return { text: '이 수강권으로는 옮길 수 없어요. 목록을 새로 불러왔어요.', reload: true };
        case 'LESSON_MOVE_TARGET_NEEDS_CONFIRM':
            return { text: '발급 기록 확인이 필요한 수강권이에요. 목록을 새로 불러왔으니 다시 골라 주세요.', reload: true };
        case 'LESSON_NOT_MOVABLE':
            return { text: '여기서 옮길 수 없는 수업이에요.', reload: true };
        case 'LESSON_MOVE_LIMIT':
            return { text: '이 수업은 더 옮길 수 없어요.', reload: false };
        case 'LESSON_MOVE_BUSY':
            return { text: '잠시 뒤 다시 해 주세요.', reload: false };
        case 'NOT_FOUND':
            return { text: '수업이나 수강권을 찾지 못했어요. 목록을 새로 불러왔어요.', reload: true };
        case 'VIEW_ONLY':
            return { text: '열람 중에는 옮길 수 없어요.', reload: false };
        case 'MOVE_ID_REUSED':
        default:
            return { text: '지금은 옮길 수 없어요. 잠시 뒤 다시 해 주세요.', reload: true };
    }
};

/**
 * 결과를 모를 때(응답 없음·503) 다시 받은 목록의 그 수업 위치로 다음 할 일을 정한다(§4-8 6번).
 * - 대상에 있으면 옮겨져 있다고만 알린다. 다시 보내지 않는다(그 사이 되돌려졌으면 보낸 요청이 실제로 다시 옮길 수 있다).
 * - 원래 수강권에 있으면 사람에게 묻는다(같은 moveId 를 보내면 실제로 옮겨질 수 있다).
 * - 다른 수강권·목록에 없음 — 다른 곳에서 바뀐 것. 보내지 않는다.
 */
export type UnknownResolution = 'confirmAtTarget' | 'askAtSource' | 'changedElsewhere';
export const resolveUnknownMove = (
    currentMembershipId: string | null,
    move: { sourceMembershipId: string; targetMembershipId: string },
): UnknownResolution => {
    if (currentMembershipId && currentMembershipId === move.targetMembershipId) return 'confirmAtTarget';
    if (currentMembershipId && currentMembershipId === move.sourceMembershipId) return 'askAtSource';
    return 'changedElsewhere';
};

/** 옮긴 뒤 다시 받아야 할 캐시(세션 현황 모든 달·강사, 일정, 수강권, 변경 이력, 급여, 잔여 정산). */
export const invalidateAfterLessonMove = (queryClient: QueryClient) => {
    const keys: readonly unknown[][] = [
        [...sessionReportKeys.all],
        ['getDailyScheduleAPI'],
        ['getWeeklyScheduleAPI'],
        ['getSmartRangeSchedulesAPI'],
        ['getListRangeSchedulesAPI'],
        ['getMembershipForMemberQuery'],
        ['getAllMembershipForMemberQuery'],
        [...membershipChangeLogKeys.all],
        ['getSalaryCalcsQuery'],
        ['getCenterRemainingScheduleFeeQuery'],
    ];
    keys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
};
